import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsEnabled, record, startAnalytics } from './analytics';

/** Nearest meaningful element + a short label for a click target. */
function describeTarget (target: EventTarget | null): { tag: string; label?: string } {
  if (!(target instanceof Element)) return { tag: 'unknown' };
  const el = (target.closest('button, a, [role="button"], [aria-label]') as HTMLElement | null) ?? target;
  const tag = el.tagName.toLowerCase();
  const raw = el.getAttribute('aria-label') || el.textContent || '';
  const label = raw.replace(/\s+/g, ' ').trim().slice(0, 60);
  return { tag, label: label || undefined };
}

/**
 * Records click positions and per-page dwell times. Mounted once, inside the
 * router so `useLocation` resolves. Completely inert unless the `uxAnalytics`
 * setting is on (see `analytics.ts`).
 */
export function useAnalytics (): void {
  const { pathname } = useLocation();
  const enterRef = useRef<number>(Date.now());
  const routeRef = useRef<string>(pathname);

  // Start the flush loop once.
  useEffect(() => {
    startAnalytics();
  }, []);

  // Global click capture — record where the user tapped, on whichever route is
  // current at click time.
  useEffect(() => {
    const onPointerUp = (e: PointerEvent) => {
      if (!analyticsEnabled()) return;
      const { tag, label } = describeTarget(e.target);
      record({
        type: 'click',
        t: Date.now(),
        route: routeRef.current,
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        vw: window.innerWidth,
        vh: window.innerHeight,
        tag,
        label,
      });
    };
    window.addEventListener('pointerup', onPointerUp, true);
    return () => window.removeEventListener('pointerup', onPointerUp, true);
  }, []);

  // Dwell: when the route changes, log how long the previous route was shown.
  useEffect(() => {
    const prev = routeRef.current;
    if (prev === pathname) return;
    const now = Date.now();
    record({ type: 'dwell', route: prev, enterT: enterRef.current, exitT: now, durationMs: now - enterRef.current });
    enterRef.current = now;
    routeRef.current = pathname;
  }, [pathname]);

  // Final dwell on page hide (covers idle-reset reloads and kiosk shutdown).
  useEffect(() => {
    const onHide = () => {
      const now = Date.now();
      record({ type: 'dwell', route: routeRef.current, enterT: enterRef.current, exitT: now, durationMs: now - enterRef.current });
      enterRef.current = now;
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);
}
